import { Accessor, createMemo, createSignal, Setter } from 'solid-js';
import {
  IDataLoadingProvider,
  IDataLoadingService,
  LoadableData,
  LoadableItem,
  LoadState,
} from './data-loading.types';
import { createLoadQueue } from './load-queue';
import { isItemLoadingOrLoaded } from './load-state-helpers';
import { LoadQueue } from './load-queue.types';
import {
  DataLoadingServiceConfig,
  HandlerRegistration,
} from './data-loading-service.types';

/**
 * Generic data loading service that orchestrates loading/unloading items
 * into capability providers via ProviderHandlers.
 *
 * Apps configure it with their specific handler registrations, state shape,
 * and slice keys. The service handles queuing, provider lifecycle, and
 * exposes a reactive `items` accessor.
 */
export class DataLoadingService<
  TItemType extends string,
  TState extends LoadableData,
> implements IDataLoadingService
{
  public readonly items: Accessor<LoadableItem[]>;
  public readonly storeSliceKeys: Accessor<(keyof TState)[]>;

  public readonly dataState: TState;

  private readonly setProviders: Setter<IDataLoadingProvider[]>;
  public readonly providers: Accessor<IDataLoadingProvider[]>;

  private readonly queues: Map<string, LoadQueue>;
  private readonly config: DataLoadingServiceConfig<TItemType, TState>;

  constructor(config: DataLoadingServiceConfig<TItemType, TState>) {
    this.config = config;
    this.dataState = config.dataState;

    const [p, setP] = createSignal<IDataLoadingProvider[]>([]);
    this.setProviders = setP;
    this.providers = p;

    const allHandlers = [
      ...config.handlers,
      ...(config.transformHandlers ?? []),
    ];
    const uniqueQueueKeys = [
      ...new Set(allHandlers.map((reg) => reg.queueKey)),
    ];
    this.queues = new Map(
      uniqueQueueKeys.map((key) => [
        key,
        createLoadQueue({ concurrency: config.queueConcurrency?.[key] }),
      ])
    );

    this.storeSliceKeys = createMemo(() => {
      const handlerSliceKeys = config.handlers.map((reg) => reg.sliceKey);
      const transformSliceKeys =
        config.transformHandlers?.map((reg) => reg.sliceKey) ?? [];
      return [...new Set([...handlerSliceKeys, ...transformSliceKeys])];
    });

    this.items = createMemo(() => {
      const items: LoadableItem[] = this.storeSliceKeys().flatMap(
        (sliceKey) => {
          const slice = config.dataState[sliceKey];
          if (slice.state !== LoadState.LoadComplete) return [];

          return Object.values(slice.items);
        }
      );
      return items;
    });
  }

  public load(type: TItemType, itemId: string): void {
    const reg = this.getRegistration(type);
    reg.handler.setLoadState(itemId, LoadState.Loading);

    this.enqueue(
      reg,
      async () => {
        const item = this.getItem(reg, itemId);
        if (!item) return;
        await reg.handler.load(item, this.providers());
      },
      itemId
    );
  }

  public unload(type: TItemType, itemId: string): void {
    const reg = this.getRegistration(type);
    reg.handler.cancelOperation(itemId);

    this.enqueue(
      reg,
      async () => {
        const item = this.getItem(reg, itemId);
        if (!item) return;
        await reg.handler.unload(item, this.providers());
      },
      itemId
    );
  }

  public reload(type: TItemType, itemIds: string[]): void {
    if (itemIds.length === 0) return;

    const reg = this.getRegistration(type);
    for (const itemId of itemIds) {
      reg.handler.cancelOperation(itemId);
    }

    this.enqueue(reg, async () => {
      const providers = this.providers();
      const reloadPromises: Promise<void>[] = itemIds.map(async (itemId) => {
        const item = this.getItem(reg, itemId);
        if (!item) return;
        await reg.handler.reload(item, providers);
      });
      await Promise.all(reloadPromises);
    });
  }

  public loadExclusive(type: TItemType, itemIds: string[]): void {
    const reg = this.getRegistration(type);

    this.enqueue(reg, async () => {
      const allItems = this.getAllItems(reg);
      if (!allItems) return;

      const providers = this.providers();
      const toUnload: string[] = [];
      const toLoad: string[] = [];

      for (const [id, item] of Object.entries(allItems)) {
        const isCurrentlyLoaded = isItemLoadingOrLoaded(item.loadState);
        const shouldBeLoaded = itemIds.includes(id);

        if (isCurrentlyLoaded && !shouldBeLoaded) {
          toUnload.push(id);
        } else if (!isCurrentlyLoaded && shouldBeLoaded) {
          toLoad.push(id);
        }
      }

      const unloadPromises: Promise<void>[] = toUnload.map(async (id) => {
        const item = allItems[id];
        await reg.handler.unload(item, providers);
      });

      const loadPromises: Promise<void>[] = toLoad.map(async (id) => {
        const item = allItems[id];
        await reg.handler.load(item, providers);
      });

      await Promise.all([...unloadPromises, ...loadPromises]);
    });
  }

  public cancelAllOperations(): void {
    const allHandlers = [
      ...this.config.handlers,
      ...(this.config.transformHandlers ?? []),
    ];
    for (const reg of allHandlers) {
      reg.handler.cancelAllOperations();
    }

    for (const queue of this.queues.values()) {
      queue.clear();
    }
  }

  public registerProvider(provider: IDataLoadingProvider): void {
    if (this.providers().includes(provider)) {
      return;
    }

    for (const reg of this.config.handlers) {
      this.enqueue(reg, async () => {
        await reg.handler.handleProviderRegistration(provider);
      });
    }

    this.setProviders([...this.providers(), provider]);
  }

  public async unregisterProvider(
    provider: IDataLoadingProvider
  ): Promise<void> {
    if (!this.providers().includes(provider)) {
      return;
    }

    await this.drainAllQueues();

    this.setProviders(this.providers().filter((p) => p !== provider));

    const unregPromises = this.config.handlers.map((reg) =>
      reg.handler.handleProviderUnregistration(provider)
    );
    await Promise.all(unregPromises);
  }

  private getRegistration(
    type: TItemType
  ): HandlerRegistration<TItemType, TState> {
    const allHandlers = [
      ...this.config.handlers,
      ...(this.config.transformHandlers ?? []),
    ];
    const reg = allHandlers.find((reg) => reg.type === type);
    if (!reg) {
      throw new Error(`No handler registered for type: ${type}`);
    }
    return reg;
  }

  private getItem(
    reg: HandlerRegistration<TItemType, TState>,
    itemId: string
  ): LoadableItem | undefined {
    const slice = this.config.dataState[reg.sliceKey];
    return slice.state === LoadState.LoadComplete
      ? slice.items[itemId]
      : undefined;
  }

  private getAllItems(
    reg: HandlerRegistration<TItemType, TState>
  ): Record<string, LoadableItem> | undefined {
    const slice = this.config.dataState[reg.sliceKey];
    return slice.state === LoadState.LoadComplete ? slice.items : undefined;
  }

  private enqueue(
    reg: HandlerRegistration<TItemType, TState>,
    operation: () => Promise<void>,
    key?: string
  ): void {
    const queue = this.queues.get(reg.queueKey);
    if (!queue) {
      throw new Error(`No queue found for queueKey: ${reg.queueKey}`);
    }
    queue.enqueue(operation, key);
  }

  private async drainAllQueues(): Promise<void> {
    const drainPromises = [...this.queues.values()].map((q) => q.drain());
    await Promise.all(drainPromises);
  }
}
